import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { authApi, type User } from '@/api/auth';
import { tokenStorage } from '@/utils/tokenStorage';
import { STORAGE_KEYS } from '@/constants/config';

// Auth state (Zustand + localStorage persistence).
// Note: token is also mirrored into `tokenStorage` so axios interceptors can
// pull it synchronously without having to read Zustand state.

interface AuthState {
  user: User | null;
  token: string | null;
  isHydrated: boolean;
  isAuthenticated: () => boolean;
  setSession: (user: User, token: string) => void;
  setUser: (user: User) => void;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isHydrated: false,
      isAuthenticated: () => Boolean(get().token && get().user),
      setSession: (user, token) => {
        tokenStorage.set(token);
        set({ user, token });
      },
      setUser: (user) => set({ user }),
      logout: async () => {
        try {
          await authApi.logout();
        } catch {
          // best-effort; still clear local session
        }
        tokenStorage.clear();
        set({ user: null, token: null });
      },
      hydrate: async () => {
        const token = tokenStorage.get();
        if (!token) {
          set({ isHydrated: true });
          return;
        }
        try {
          const user = await authApi.me();
          set({ user, token, isHydrated: true });
        } catch {
          tokenStorage.clear();
          set({ user: null, token: null, isHydrated: true });
        }
      },
    }),
    {
      name: STORAGE_KEYS.authUser,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ user: state.user, token: state.token }),
    },
  ),
);
